import type { BreakdownRow, FilterCondition, FilterDefinition, ReportChange, ReportDimension } from '@fint/validation';
import type { Basis } from '@/features/dashboard/shared';

/**
 * `FILTER_QUERY_PARAM` from @fint/validation, spelled out: this module is
 * type-only on the package so the unit tests can load it under plain node,
 * and the e2e click-through pins the two to the same word.
 */
const WHERE = 'where';

/**
 * Where a bar goes when it is clicked, and how a change reads.
 *
 * ── THE CLICK ──
 * Every row of a breakdown is the sum of records a list can show, so the
 * click opens that list with the advanced filter set to exactly those
 * records: the invoices carrying this item in this window, the expenses in
 * this category. The filter is the same `where` a person could have built
 * by hand, so what opens is a normal, editable list - not a dead end.
 *
 * On the cash basis an income row is "what was settled in the window", and
 * an invoice list has no "paid between" condition, so the date falls away
 * and the label says so ("the invoices with this item").
 */
export interface Drill {
  href: string;
  /** What the click does, in words the hover shows: "Open the 9 invoices". */
  label: string;
}

const between = (field: string, from: string, to: string): FilterCondition => ({ field, operator: 'between', value: [from, to] });
const isIn = (field: string, id: string): FilterCondition => ({ field, operator: 'in', value: [id] });
const isEmpty = (field: string): FilterCondition => ({ field, operator: 'is_empty' });
const eq = (field: string, value: string): FilterCondition => ({ field, operator: 'eq', value });

function listUrl(path: string, conditions: FilterCondition[]): string {
  const definition: FilterDefinition = { conjunction: 'and', conditions };
  return `${path}?${WHERE}=${encodeURIComponent(JSON.stringify(definition))}`;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export function drillFor(
  side: 'income' | 'expense',
  by: ReportDimension,
  row: BreakdownRow,
  window: { from: string; to: string },
  basis: Basis,
): Drill | null {
  const id = row.key.slice(row.key.indexOf(':') + 1);
  if (side === 'expense') {
    const conditions: FilterCondition[] = [between('date', window.from, window.to)];
    switch (row.kind) {
      case 'category':
        conditions.push(isIn('categoryId', id));
        break;
      case 'vendor':
        conditions.push(isIn('vendorId', id));
        break;
      case 'project':
        conditions.push(isIn('projectId', id));
        break;
      case 'none':
        conditions.push(isEmpty(by === 'vendor' ? 'vendorId' : 'projectId'));
        break;
      default:
        return null;
    }
    return { href: listUrl('/expenses', conditions), label: `Open the ${plural(row.count, 'expense')}` };
  }

  // Income. Deposits with no invoice live on the banking list.
  if (row.kind === 'deposits') {
    const conditions: FilterCondition[] = [between('date', window.from, window.to)];
    return { href: listUrl('/banking', conditions), label: `Open the ${plural(row.count, 'deposit')} on the bank list` };
  }
  const conditions: FilterCondition[] = basis === 'accrual' ? [between('date', window.from, window.to)] : [];
  switch (row.kind) {
    case 'item':
      conditions.push(isIn('itemId', id));
      break;
    case 'line-name':
      conditions.push(eq('lineName', row.name));
      break;
    case 'hsn':
      conditions.push(eq('hsnSac', id));
      break;
    case 'customer':
      conditions.push(isIn('customerId', id));
      break;
    case 'category':
      conditions.push(isIn('incomeCategoryId', id));
      break;
    case 'none':
      if (by === 'hsn') conditions.push(isEmpty('hsnSac'));
      else if (by === 'category') conditions.push(isEmpty('incomeCategoryId'));
      else return null;
      break;
    default:
      return null;
  }
  const noun = { item: 'item', hsn: 'code', customer: 'customer', category: 'category' }[by as 'item' | 'hsn' | 'customer' | 'category'] ?? 'item';
  const what = basis === 'accrual' ? `Open the ${plural(row.count, 'invoice')}` : `Open the invoices with this ${noun}`;
  return { href: listUrl('/invoices', conditions), label: what };
}

/** The list a month of the stacked chart opens: every expense dated in it. */
export function drillForMonth(side: 'income' | 'expense', ym: string, basis: Basis): Drill {
  const [y, m] = ym.split('-').map(Number);
  const last = new Date(Date.UTC(y ?? 1970, m ?? 1, 0)).getUTCDate();
  const from = `${ym}-01`;
  const to = `${ym}-${String(last).padStart(2, '0')}`;
  if (side === 'expense') return { href: listUrl('/expenses', [between('date', from, to)]), label: 'Open the month on the expenses list' };
  return { href: basis === 'accrual' ? listUrl('/invoices', [between('date', from, to)]) : '/invoices', label: 'Open the month on the invoices list' };
}

/**
 * A change, as the hover says it: "+₹4,10,000 · +29%". A null percent
 * (nothing in the earlier window) reads as "new" - an infinite percentage is
 * not a number anyone reads. Whether a rise is good news depends on the side.
 */
export function describeChange(change: ReportChange, formatMoney: (amount: string) => string): { text: string; tone: 'up' | 'down' | 'flat' } {
  const delta = Number(change.delta);
  const tone: 'up' | 'down' | 'flat' = delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat';
  const sign = delta > 0 ? '+' : delta < 0 ? '−' : '';
  const amount = formatMoney(Math.abs(delta).toFixed(2));
  if (change.percent === null) return { text: delta === 0 ? `nothing either time` : `${sign}${amount} · new`, tone };
  const percent = Math.round(Math.abs(change.percent) * 100);
  return { text: `${sign}${amount} · ${sign}${percent}%`, tone };
}

/** "Apr–Sep 2025", "Aug 2026", "10–19 Aug 2026": the earlier window, named. */
export function windowLabel(change: ReportChange): string {
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const [fy, fm, fd] = change.from.split('-').map(Number);
  const [ty, tm, td] = change.to.split('-').map(Number);
  const wholeFrom = fd === 1;
  const lastOfTo = new Date(Date.UTC(ty ?? 1970, tm ?? 1, 0)).getUTCDate();
  const wholeTo = td === lastOfTo;
  const month = (m?: number) => MONTHS[(m ?? 1) - 1];
  if (wholeFrom && wholeTo) {
    if (fy === ty && fm === tm) return `${month(fm)} ${fy}`;
    if (fy === ty) return `${month(fm)}–${month(tm)} ${fy}`;
    return `${month(fm)} ${fy}–${month(tm)} ${ty}`;
  }
  if (fy === ty && fm === tm) return `${fd}–${td} ${month(fm)} ${fy}`;
  return `${fd} ${month(fm)} ${fy}–${td} ${month(tm)} ${ty}`;
}
